// Libraries
import { TypeOf, z } from 'zod';

export const CiiRatingSchema = z.enum(['A', 'B', 'C', 'D', 'E']).optional();
export type CiiRating = TypeOf<typeof CiiRatingSchema>;

export const FuelOptionSchema = z.object({
  label: z.string(),
  value: z.number(),
  price: z.number(),
  co2ConversionFactor: z.number(),
  energyConversion: z.number(),
});
export type FuelOption = TypeOf<typeof FuelOptionSchema>;

export const FuelSchema = z.object({
  name: z.string(),
  displayName: z.string(),
  category: z.string(),
  costMTUsd: z.string(),
  proportion: z.string(),
  conversionFactor: z.string(),
});
export type Fuel = TypeOf<typeof FuelSchema>;

export const FuelModeSchema = z.object({
  mode: z.string(),
  percentage: z.string(),
  fuels: z.array(FuelSchema),
});
export type FuelMode = TypeOf<typeof FuelModeSchema>;

export const FuelTypeSchema = z.object({
  energyConversion: z.number(),
  co2ConversionFactor: z.number(),
  id: z.number(),
  maxPrice: z.number(),
  minPrice: z.number(),
  name: z.string(),
  displayName: z.string(),
  price: z.number(),
});

export const ComponentAnalysisSchema = z.object({
  foulingContributionPercentage: z.number(),
  speedContributionPercentage: z.number(),
  weatherContributionPercentage: z.number(),
});

export const UnderwaterCleaningSchema = z.object({
  underWaterCleaningCo2Change: z.number(),
  underWaterCleaningFocChange: z.number(),
  underWaterCleaningPercentageChange: z.number(),
});

const VesselSchema = z.object({
  beam: z.number(),
  clientId: z.number(),
  deadWeightTonnage: z.number(),
  department: z.string(),
  displacementAtBallast: z.number(),
  displacementAtLaden: z.number(),
  forecastedAverageBeaufort: z.number(),
  forecastedAverageSpeed: z.number(),
  forecastedCiiAttained: z.number(),
  forecastedCiiRating: z.string(),
  requiredCiiRating: z.string().optional(),
  forecastedCo2: z.number(),
  forecastedDistanceSailed: z.number(),
  forecastedFoc: z.number(),
  forecastedRemainingDaysOfSailing: z.number(),
  grossTonnage: z.number(),
  imo: z.number(),
  lastNoonReportDate: z.string(),
  lastNoonReportDateUTC: z.string(),
  length: z.number(),
  maxCapacity: z.number(),
  maxSpeed: z.number(),
  minSpeed: z.number(),
  mostUsedFuelTypeId: z.number(),
  mostUsedFuelTypePercentage: z.number(),
  name: z.string(),
  primaryFuelType: z.string(),
  primaryFuelTypeId: z.number(),
  requiredCiiScore: z.number(),
  currentYearRequiredCiiScore: z.number(),
  secondaryFuelType: z.string(),
  secondaryFuelTypeId: z.number(),
  secondaryFuelTypePercentage: z.number(),
  segment: z.string(),
  shipyard: z.string(),
  type: z.string(),
  vesselDeadWeightTonnage: z.number(),
  vesselName: z.string(),
  vesselType: z.string(),
  vesselTypeId: z.number(),
  year: z.number(),
  yearBuilt: z.number(),
  yearToDateAverageSpeed: z.number(),
  yearToDateCiiAttained: z.number(),
  yearToDateCiiRating: z.string(),
  yearToDateCo2: z.number(),
  yearToDateDistanceSailed: z.number(),
  yearToDateFoc: z.number(),
});

const CiiRatingBoundarySchema = z.tuple([z.number(), z.number()]);

const CiiRatingBoundariesSchema = z.object({
  A: CiiRatingBoundarySchema,
  B: CiiRatingBoundarySchema,
  C: CiiRatingBoundarySchema,
  D: CiiRatingBoundarySchema,
  E: CiiRatingBoundarySchema,
});

const RangeSchema = z.object({
  maxValue: z.number(),
  minValue: z.number(),
});

export const ParametersRangesSchema = z.object({
  averageBeaufortScale: RangeSchema,
  averageSpeed: RangeSchema,
  carbonCredit: RangeSchema,
  daysSailingPerMonth: RangeSchema,
});

const SimulationStateSchema = z.object({
  averageBeaufort: z.number(),
  averageDaysOfSailingPerMonth: z.number(),
  averageSpeedKts: z.number(),
  ballastAverageSpeedKts: z.number(),
  carbonCreditPricePerMT: z.number(),
  enableSecondaryFuel: z.boolean(),
  fuelModes: z.array(FuelModeSchema),
  fuelTypeConversionFactor: z.number(),
  energyConversion: z.number(),
  ladenAverageSpeedKts: z.number(),
  mostUsedFuelType: z.string(),
  mostUsedFuelTypePercentage: z.number(),
  primaryFuelType: z.string(),
  primaryFuelTypeId: z.number(),
  secondaryFuelType: z.string(),
  secondaryFuelTypeId: z.number(),
  secondaryFuelTypePercentage: z.number(),
  useEOYCorrectionFactor: z.boolean(),
  useUnderWaterCleaning: z.boolean(),
  defaultFuelPrice: z.number(),
  defaultFuelType: FuelOptionSchema,
  bulkFuelType: FuelOptionSchema.optional(),
  bulkFuelPrice: z.number().optional(),
  bulkFuelQuantity: z.number().optional(),
  bulkFuelEnabled: z.boolean().optional(),
});

const CIISimulatorSchema = z.object({
  ciiRatingBoundaries: CiiRatingBoundariesSchema,
  componentAnalysis: ComponentAnalysisSchema,
  fuelTypes: z.array(FuelTypeSchema),
  initialSimulationState: SimulationStateSchema,
  operationalYear: z.number(),
  parametersConfiguration: ParametersRangesSchema,
  underwaterCleaning: UnderwaterCleaningSchema,
  vessel: VesselSchema,
});

const CiiScoreSchema = z.object({
  cii: z.number(),
  time: z.string(),
});

const SimulatedCIIScoreSchema = CiiScoreSchema.merge(
  z.object({
    upperCII: z.number(),
    lowerCII: z.number(),
  })
);

const EmissionsImpactSchema = z.object({
  carbonCreditPricePerMT: z.number(),
  fuelPricePerMT: z.number(),
  mileageNM: z.number(),
  totalEmissionsMT: z.number(),
});

const GraphicsDataSchema = z.object({
  yearToDateCIIScores: z.array(CiiScoreSchema),
  simulatedCIIScores: z.array(SimulatedCIIScoreSchema),
  bulkUsageEndIndex: z.number().optional(),
});

const SimulateParametersSchema = SimulationStateSchema.pick({
  averageBeaufort: true,
  averageDaysOfSailingPerMonth: true,
  ladenAverageSpeedKts: true,
  ballastAverageSpeedKts: true,
  carbonCreditPricePerMT: true,
  defaultFuelType: true,
  fuelTypeConversionFactor: true,
  energyConversion: true,
  useUnderWaterCleaning: true,
  defaultFuelPrice: true,
  bulkFuelType: true,
  bulkFuelPrice: true,
  bulkFuelQuantity: true,
  bulkFuelEnabled: true,
});

const DefaultParametersValuesSchema = z.object({
  fuelType: FuelOptionSchema,
  fuelPrice: z.number(),
  carbonPrice: z.number(),
  avgSpeed: z.number().optional(),
  ladenAvgSpeed: z.number(),
  ballastAvgSpeed: z.number(),
  sailingDays: z.number(),
  avgBeaufort: z.number(),
  uwc: z.boolean(),
  fuelModes: z.array(FuelModeSchema),
  bulkFuelType: FuelOptionSchema.optional().nullable(),
  bulkFuelPrice: z.number().optional(),
  bulkFuelQuantity: z.number().optional(),
  bulkFuelEnabled: z.boolean(),
});

export type DefaultParametersValues = TypeOf<typeof DefaultParametersValuesSchema>;
export type CIISimulatorState = TypeOf<typeof CIISimulatorSchema>;
export type SimulateParameters = TypeOf<typeof SimulateParametersSchema>;
export type GraphicsData = TypeOf<typeof GraphicsDataSchema>;
export type CiiRatingBoundaries = TypeOf<typeof CiiRatingBoundariesSchema>;
export type ParametersRanges = TypeOf<typeof ParametersRangesSchema>;
export type EmissionsImpact = TypeOf<typeof EmissionsImpactSchema>;
export type UnderwaterCleaning = TypeOf<typeof UnderwaterCleaningSchema>;
